import { Application, Container, Graphics } from "pixi.js";
import type { TaskDefinition } from "@talent/protocol";
import {
  SessionRecorder,
  TouchTracker,
  detectImplausibleSpeed,
  detectMultiTouch,
} from "@talent/client-telemetry";
import type { SequenceStimulus } from "@talent/tasks";
import {
  ABANDONED,
  TIMED_OUT,
  animate,
  burst,
  easeOutBack,
  glow,
  nudge,
  pop,
  prefersReducedMotion,
  ripple,
  untilSettled,
  wait,
} from "./game-feel.js";
import { advance, newLadder } from "./ladder.js";
import { cellTone, missTone, successChime } from "./sound.js";
import { drawToken } from "./token.js";
import { COLOR, familyColor, lighten, mix, touchMin } from "./paint.js";

/**
 * Sequence completion — the pattern repeats, so what goes in the gap?
 *
 * **The strip runs right to left.** A Persian-speaking child scans that way, so the sequence starts
 * at the right edge and the gap falls on the left, where their eye arrives last. Laid out the other
 * way the gap is the first thing they see and the pattern reads backwards, which turns a task about
 * noticing repetition into a task about overriding a reading habit.
 *
 * **Colour is never the only cue.** Shape varies at every difficulty and colour is added on top, so
 * a colour-blind child has a complete route to every answer. That falls out of the generator rather
 * than being a rule the renderer has to keep.
 */

export interface SequenceTaskResult {
  reason: "completed" | "abandoned" | "ceiling" | "floor" | "error";
  trialsAttempted: number;
  trialsCorrect: number;
  rawScore: number;
}

interface RunOptions {
  canvasParent: HTMLElement;
  task: TaskDefinition;
  formId: string;
  recorder: SessionRecorder;
  generateStimulus: (
    task: TaskDefinition,
    formId: string,
    trialIndex: number,
    difficulty: number,
  ) => SequenceStimulus;
  scoreTrial: (correctIndex: number, chosenIndex: number) => boolean;
  signal?: AbortSignal;
  /**
   * The level to open at, from the server's suggestion for this child.
   *
   * Absent for free play, which opens at the game's own floor. Clamped into range rather than
   * trusted: it is derived from an objective's level window, which lives in a different file from
   * this task's ceiling.
   */
  startAt?: number;
}

export async function runSequenceTask(opts: RunOptions): Promise<SequenceTaskResult> {
  const { task, recorder, formId } = opts;
  const accent = familyColor(task.family);
  const idle = (ms: number) => wait(ms, opts.signal);

  const app = new Application();
  await app.init({
    background: COLOR.stageDeep,
    resizeTo: opts.canvasParent,
    antialias: true,
    resolution: Math.min(window.devicePixelRatio || 1, 2),
    autoDensity: true,
  });
  // The play area is shared between games and must hold exactly one canvas: two stacked canvases
  // both keep their pointer handlers, which presents as taps going to the wrong game.
  opts.canvasParent.replaceChildren(app.canvas);

  const backdrop = new Graphics();
  const glowLayer = new Container();
  const stage = new Container();
  const fx = new Container();
  app.stage.addChild(backdrop, glowLayer, stage, fx);

  const touch = new TouchTracker();
  const onPointerDown = (e: PointerEvent) => touch.down(e.pointerId);
  const onPointerUp = (e: PointerEvent) => touch.up(e.pointerId);
  app.canvas.addEventListener("pointerdown", onPointerDown);
  app.canvas.addEventListener("pointerup", onPointerUp);
  app.canvas.addEventListener("pointercancel", onPointerUp);

  function paintBackdrop(): void {
    const w = app.renderer.width;
    const h = app.renderer.height;
    backdrop.clear();
    for (let i = 0; i < 24; i++) {
      backdrop.rect(0, (h / 24) * i, w, h / 24 + 1).fill(mix(COLOR.stage, COLOR.stageDeep, i / 24));
    }
    backdrop.circle(w / 2, h * 0.3, Math.min(w, h) * 0.5).fill({ color: accent, alpha: 0.08 });
  }
  paintBackdrop();
  app.renderer.on("resize", paintBackdrop);

  function present(stimulus: SequenceStimulus): {
    chosen: Promise<number>;
    relayout: () => void;
    cleanup: () => void;
  } {
    const layer = new Container();
    stage.addChild(layer);
    const strip = new Container();
    const row = new Container();
    layer.addChild(strip, row);

    const optionGraphics: Graphics[] = [];
    let resolveChoice: ((index: number) => void) | null = null;
    let accepting = true;
    let optionSize = 0;
    /** The empty slot in the strip: where a chosen piece flies to, and where the answer lands. */
    let gapGraphic: Graphics | null = null;
    let gapX = 0;
    let gapY = 0;
    let tokenSize = 0;

    function layout(): void {
      const w = app.renderer.width;
      const h = app.renderer.height;

      // The strip, including its gap, has to fit the width at a legible size.
      const slots = stimulus.visible.length + 1;
      const stripGap = Math.max(6, w * 0.016);
      tokenSize = Math.min(
        (w * 0.92 - stripGap * (slots - 1)) / slots,
        h * 0.16,
        76,
      );
      const stripWidth = tokenSize * slots + stripGap * (slots - 1);
      // Right to left: the first token sits at the right edge and the gap ends up on the left.
      const rightEdge = (w + stripWidth) / 2 - tokenSize / 2;
      const stripY = h * 0.3;

      strip.removeChildren().forEach((c) => c.destroy());
      stimulus.visible.forEach((token, i) => {
        const g = new Graphics();
        drawToken(g, token, tokenSize, "idle", accent);
        g.position.set(rightEdge - i * (tokenSize + stripGap), stripY);
        strip.addChild(g);
      });
      gapX = rightEdge - stimulus.visible.length * (tokenSize + stripGap);
      gapY = stripY;
      const gap = new Graphics();
      drawToken(gap, null, tokenSize, "gap", accent);
      gap.position.set(gapX, gapY);
      strip.addChild(gap);
      gapGraphic = gap;
      glow(glowLayer, gapX, gapY, tokenSize * 0.7, accent, 900);

      // Options: as many columns as fit at the touch minimum, so nothing is ever cramped off
      // the edge of a narrow screen.
      const count = stimulus.options.length;
      const optionGap = Math.max(10, w * 0.03);
      const columns = Math.max(2, Math.min(count, Math.floor((w * 0.92 + optionGap) / (touchMin() + optionGap))));
      const rows = Math.ceil(count / columns);
      optionSize = Math.max(
        touchMin(),
        Math.min((w * 0.92 - optionGap * (columns - 1)) / columns, h * 0.2),
      );
      const gridW = optionSize * columns + optionGap * (columns - 1);
      const startX = (w - gridW) / 2 + optionSize / 2;
      const startY = h * 0.62 - ((rows - 1) * (optionSize + optionGap)) / 2;

      row.removeChildren();
      optionGraphics.length = 0;
      stimulus.options.forEach((token, i) => {
        const g = new Graphics();
        drawToken(g, token, optionSize, "idle", accent);
        // Right to left here too, so the options read in the same direction as the strip.
        const col = columns - 1 - (i % columns);
        g.position.set(
          startX + col * (optionSize + optionGap),
          startY + Math.floor(i / columns) * (optionSize + optionGap),
        );
        g.eventMode = "static";
        g.cursor = "pointer";
        const half = optionSize / 2 + optionSize * 0.12;
        g.hitArea = {
          contains: (x: number, y: number) => x >= -half && y >= -half && x <= half && y <= half,
        };
        g.on("pointertap", () => {
          if (!accepting || !resolveChoice) return;
          accepting = false;
          ripple(fx, g.position.x, g.position.y, accent, optionSize);
          resolveChoice(i);
        });
        optionGraphics.push(g);
        row.addChild(g);
      });
    }

    layout();
    if (!prefersReducedMotion()) {
      optionGraphics.forEach((g, i) => {
        g.scale.set(0);
        void idle(100 + i * 60).then(() => animate(280, (t) => g.scale.set(easeOutBack(t))));
      });
    }

    /**
     * Carries the chosen piece up into the gap.
     *
     * The verdict belongs IN the sequence, not under it. Judging the choice down in the option row
     * tells a child whether they were right without ever showing them the thing that makes it
     * right — the completed strip. Watching the piece land and the pattern either run on or break
     * is the explanation, and it is one no text has to give.
     */
    async function flyToGap(from: Graphics, token: number): Promise<void> {
      const flyer = new Graphics();
      drawToken(flyer, token, optionSize, "idle", accent);
      flyer.position.copyFrom(from.position);
      fx.addChild(flyer);
      from.alpha = 0.25;

      const startX = from.position.x;
      const startY = from.position.y;
      const scaleTo = tokenSize / optionSize;

      if (prefersReducedMotion()) {
        flyer.position.set(gapX, gapY);
      } else {
        await animate(300, (t) => {
          const e = easeOutBack(Math.min(1, t));
          flyer.position.set(startX + (gapX - startX) * e, startY + (gapY - startY) * e);
          flyer.scale.set(1 + (scaleTo - 1) * e);
        });
      }
      flyer.destroy();
    }

    const chosen = new Promise<number>((resolve) => {
      resolveChoice = resolve;
    }).then(async (index) => {
      const correct = index === stimulus.correctIndex;
      const picked = optionGraphics[index];
      const token = stimulus.options[index] ?? null;

      if (picked && token !== null) await flyToGap(picked, token);

      if (correct) {
        successChime();
        if (gapGraphic) {
          drawToken(gapGraphic, token, tokenSize, "right", accent);
          burst(fx, gapX, gapY, [accent, lighten(accent, 0.4), COLOR.correct]);
          await pop(gapGraphic, { scale: 1.22 });
        }
        await idle(420);
      } else {
        missTone();
        if (gapGraphic) {
          // The wrong piece sits in the slot first, so the child sees the pattern break where it
          // breaks.
          drawToken(gapGraphic, token, tokenSize, "wrong", accent);
          await nudge(gapGraphic);
          await idle(320);
          // Then it is replaced by the piece that does continue the sequence.
          drawToken(
            gapGraphic,
            stimulus.options[stimulus.correctIndex] ?? null,
            tokenSize,
            "right",
            accent,
          );
          glow(glowLayer, gapX, gapY, tokenSize * 0.7, COLOR.correct, 700);
          cellTone(4, { volume: 0.09 });
          await pop(gapGraphic, { scale: 1.16 });
        }
        // The option that WOULD have been right is also marked, so the child can connect the
        // piece in the strip back to the one they could have picked.
        const answer = optionGraphics[stimulus.correctIndex];
        if (answer) {
          drawToken(answer, stimulus.options[stimulus.correctIndex] ?? null, optionSize, "right", accent);
        }
        await idle(700);
      }
      return index;
    });

    return {
      chosen,
      relayout: layout,
      cleanup: () => {
        accepting = false;
        layer.destroy({ children: true });
      },
    };
  }

  // The level to open at comes from the server's suggestion — the objective the child is working
  // on — and is clamped into this task's range by `newLadder`.
  let ladder = newLadder(task, opts.startAt);
  let difficulty = ladder.difficulty;
  let trialIndex = 0;
  let correctCount = 0;
  let reason: SequenceTaskResult["reason"] = "completed";

  recorder.enterTask(task.id, task.version);
  await recorder.record("task_start", {
    formId,
    startingDifficulty: difficulty,
    narrative: task.presentation.narrative,
    calibrationVersion: task.measurement.calibrationVersion,
  });

  let onResize: (() => void) | null = null;

  try {
    for (;;) {
      const stimulus = opts.generateStimulus(task, formId, trialIndex, difficulty);

      recorder.setInTrial(true);
      await recorder.record("trial_start", {
        trialIndex,
        difficulty,
        stimulus: stimulus.visible,
        // Options and the answer, so the trial stays re-scorable from the record alone.
        options: stimulus.options.map((token) => [token]),
        correctIndex: stimulus.correctIndex,
      });

      const start = performance.now();
      const trial = present(stimulus);
      // Resize is bound per trial: the layout closure belongs to the trial on screen, and a
      // handler outliving it would redraw a destroyed container.
      onResize = trial.relayout;
      app.renderer.on("resize", trial.relayout);

      const outcome = await untilSettled(trial.chosen, opts.signal, task.measurement.trialTimeoutMs);
      const elapsed = performance.now() - start;
      app.renderer.off("resize", trial.relayout);
      onResize = null;
      trial.cleanup();

      if (outcome === ABANDONED || outcome === TIMED_OUT) {
        /**
         * Stopping and leaving both end the sitting, and only one of them is recorded.
         *
         * A timeout means the child put the phone down mid-trial. It is `abandoned` rather than a
         * wrong answer on purpose: we know they stopped and we do not know they could not do it, and
         * scoring silence as failure is the mistake `stopAfterErrors` used to make.
         */
        if (outcome === TIMED_OUT) {
          await recorder.record("trial_timeout", { trialIndex, elapsedMs: elapsed });
        }
        recorder.setInTrial(false);
        reason = "abandoned";
        break;
      }

      const correct = opts.scoreTrial(stimulus.correctIndex, outcome);
      if (correct) correctCount++;

      await recorder.record("trial_response", {
        trialIndex,
        response: [outcome],
        correct,
        responseMs: elapsed,
      });

      const fast = detectImplausibleSpeed(elapsed, stimulus.options.length);
      if (fast) await recorder.record("input_anomaly", fast);
      const multi = detectMultiTouch(touch.peak);
      if (multi) await recorder.record("input_anomaly", multi);
      touch.resetPeak();

      recorder.setInTrial(false);
      trialIndex++;

      /**
       * The teaching policy lives in `ladder.ts`, not here.
       *
       * A streak raises the level, errors lower it, and the sitting ends when the trials run out —
       * never because a child was struggling (R-5). It was copied into all ten renderers before,
       * which is nine chances for one game to end up quietly stricter than the rest.
       */
      const step = advance(task, ladder, correct);
      if (step.change) await recorder.record("difficulty_change", step.change);
      ladder = step;
      difficulty = step.difficulty;
      if (step.finished) {
        reason = "completed";
        break;
      }
    }
  } catch {
    reason = "error";
  } finally {
    if (onResize) app.renderer.off("resize", onResize);
    await recorder.record("task_end", {
      reason,
      trialsAttempted: trialIndex,
      rawScore: trialIndex > 0 ? correctCount / trialIndex : 0,
    });
    recorder.exitTask();
    app.renderer.off("resize", paintBackdrop);
    app.canvas.removeEventListener("pointerdown", onPointerDown);
    app.canvas.removeEventListener("pointerup", onPointerUp);
    app.canvas.removeEventListener("pointercancel", onPointerUp);
    app.destroy(true, { children: true });
  }

  return {
    reason,
    trialsAttempted: trialIndex,
    trialsCorrect: correctCount,
    rawScore: trialIndex > 0 ? correctCount / trialIndex : 0,
  };
}
